import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { GameSession, GameType, GameStats, MuscleData } from '../types';
import { generateQuestions } from '../utils/gameLogic';

interface GameState {
  currentSession: GameSession | null;
  stats: GameStats;
  muscleData: MuscleData | null;
}

type GameAction =
  | { type: 'SET_MUSCLE_DATA'; payload: MuscleData }
  | { type: 'START_GAME'; payload: { gameType: GameType; seed?: number; cheatMode?: boolean } }
  | { type: 'ANSWER_QUESTION'; payload: { questionId: string; selectedAnswer: string } }
  | { type: 'END_GAME' }
  | { type: 'RESET_GAME' };

const initialState: GameState = {
  currentSession: null,
  stats: {
    totalGames: 0,
    averageScore: 0,
    bestTime: Infinity,
    gameTypeStats: {
      [GameType.NAME_MATCH]: { played: 0, averageScore: 0 },
      [GameType.INNERVATION]: { played: 0, averageScore: 0 },
      [GameType.BLOOD_SUPPLY]: { played: 0, averageScore: 0 },
      [GameType.INNERVATION_OR_BLOOD]: { played: 0, averageScore: 0 },
      [GameType.ORIGIN_INSERTION]: { played: 0, averageScore: 0 }
    }
  },
  muscleData: null
};

const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'SET_MUSCLE_DATA':
      return {
        ...state,
        muscleData: action.payload
      };

    case 'START_GAME':
      if (!state.muscleData) return state;
      
      const seed = action.payload.seed || Date.now();
      const questions = generateQuestions(state.muscleData, action.payload.gameType, 15, seed);
      
      return {
        ...state,
        currentSession: {
          id: `game_${seed}`,
          type: action.payload.gameType,
          questions,
          currentQuestionIndex: 0,
          score: 0,
          startTime: Date.now(),
          answers: [],
          cheatMode: action.payload.cheatMode || false
        }
      };

    case 'ANSWER_QUESTION':
      if (!state.currentSession) return state;
      
      const currentQuestion = state.currentSession.questions[state.currentSession.currentQuestionIndex];
      const isCorrect = action.payload.selectedAnswer === currentQuestion.correctAnswer;
      
      const newAnswer = {
        questionId: action.payload.questionId,
        selectedAnswer: action.payload.selectedAnswer,
        isCorrect
      };
      
      return {
        ...state,
        currentSession: {
          ...state.currentSession,
          currentQuestionIndex: state.currentSession.currentQuestionIndex + 1,
          score: state.currentSession.score + (isCorrect ? 1 : 0),
          answers: [...state.currentSession.answers, newAnswer]
        }
      };

    case 'END_GAME':
      if (!state.currentSession) return state;
      
      const endTime = Date.now();
      const gameTime = endTime - state.currentSession.startTime;
      const finalScore = state.currentSession.score;
      const gameType = state.currentSession.type;
      
      // 統計を更新
      const newStats = { ...state.stats };
      newStats.totalGames += 1;
      newStats.averageScore = (newStats.averageScore * (newStats.totalGames - 1) + finalScore) / newStats.totalGames;
      
      if (gameTime < newStats.bestTime) {
        newStats.bestTime = gameTime;
      }
      
      const typeStats = newStats.gameTypeStats[gameType];
      typeStats.played += 1;
      typeStats.averageScore = (typeStats.averageScore * (typeStats.played - 1) + finalScore) / typeStats.played;
      
      return {
        ...state,
        currentSession: {
          ...state.currentSession,
          endTime
        },
        stats: newStats
      };

    case 'RESET_GAME':
      return {
        ...state,
        currentSession: null
      };

    default:
      return state;
  }
};

const GameContext = createContext<{
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
} | null>(null);

export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  return (
    <GameContext.Provider value={{ state, dispatch }}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};